/**
 * @module lib/menuApi
 * @description Menu builder API calls for the editor, template picker and
 * import screens (T4, spec D6/D7). Every call goes through lib/api (auth
 * headers, staff-session expiry on 401) and resolves to the parsed body or
 * throws a MenuApiError whose `kind` drives the UI:
 *   'sample'    403 SAMPLE_TENANT_READ_ONLY: signed out / demo, the sample menu
 *   'auth'      401: the session is gone, sign in again
 *   'forbidden' 403 otherwise: this role may not edit the menu
 *   'network'   the server could not be reached
 *   'other'     anything else (validation, conflict, server error)
 */

import { apiGet, apiPost, apiPut, apiDelete } from './api';

export const SAMPLE_MESSAGE = 'This is the sample menu; sign in as your restaurant to edit.';
export const REAUTH_MESSAGE = 'Your session has expired. Sign in again to keep editing the menu.';
export const FORBIDDEN_MESSAGE = 'Only an owner, admin or manager can edit the menu.';
export const NETWORK_MESSAGE = 'Cannot reach the server. Check the connection and try again.';

export class MenuApiError extends Error {
    constructor(status, body = {}) {
        let kind = 'other';
        if (status === 0) kind = 'network';
        else if (status === 401) kind = 'auth';
        else if (status === 403) kind = body.code === 'SAMPLE_TENANT_READ_ONLY' ? 'sample' : 'forbidden';
        const message = {
            sample: SAMPLE_MESSAGE, auth: REAUTH_MESSAGE, forbidden: FORBIDDEN_MESSAGE, network: NETWORK_MESSAGE,
        }[kind] || body.error || `Request failed (${status})`;
        super(message);
        this.status = status;
        this.kind = kind;
        this.body = body;
    }
}

async function call(request) {
    let res;
    try {
        res = await request();
    } catch {
        throw new MenuApiError(0);
    }
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new MenuApiError(res.status, body);
    return body;
}

const enc = encodeURIComponent;

export const menuApi = {
    manage: () => call(() => apiGet('/api/menu/manage')),
    templates: () => call(() => apiGet('/api/menu/templates')),
    applyTemplate: (id) => call(() => apiPost(`/api/menu/templates/${enc(id)}/apply`, {})),

    createCategory: (body) => call(() => apiPost('/api/menu/categories', body)),
    updateCategory: (id, body) => call(() => apiPut(`/api/menu/categories/${enc(id)}`, body)),
    deleteCategory: (id, force = false) => call(() => apiDelete(`/api/menu/categories/${enc(id)}${force ? '?force=true' : ''}`)),
    reorderCategories: (ids) => call(() => apiPut('/api/menu/categories/order', { ids })),

    createItem: (body) => call(() => apiPost('/api/menu/items', body)),
    updateItem: (id, body) => call(() => apiPut(`/api/menu/items/${enc(id)}`, body)),
    deleteItem: (id) => call(() => apiDelete(`/api/menu/items/${enc(id)}`)),
    reorderItems: (ids) => call(() => apiPut('/api/menu/items/order', { ids })),
    set86: (id, is86d) => call(() => apiPut(`/api/menu/item/${enc(id)}/86`, { is86d })),

    importMenu: ({ format, text, dryRun }) => call(() => apiPost('/api/menu/import', { format, text, dryRun })),
};

/** Colors the server accepts (services/menuBuilder.js CATEGORY_COLORS). Literal so Tailwind keeps the classes. */
export const CATEGORY_COLORS = [
    { value: 'bg-amber-600', label: 'Amber' }, { value: 'bg-green-600', label: 'Green' },
    { value: 'bg-red-700', label: 'Red' }, { value: 'bg-orange-600', label: 'Orange' },
    { value: 'bg-yellow-600', label: 'Yellow' }, { value: 'bg-pink-600', label: 'Pink' },
    { value: 'bg-rose-600', label: 'Rose' }, { value: 'bg-red-800', label: 'Wine red' },
    { value: 'bg-gray-600', label: 'Gray' }, { value: 'bg-blue-600', label: 'Blue' },
    { value: 'bg-sky-600', label: 'Sky' }, { value: 'bg-teal-600', label: 'Teal' },
    { value: 'bg-emerald-600', label: 'Emerald' }, { value: 'bg-cyan-600', label: 'Cyan' },
    { value: 'bg-indigo-600', label: 'Indigo' }, { value: 'bg-purple-600', label: 'Purple' },
    { value: 'bg-lime-600', label: 'Lime' }, { value: 'bg-stone-600', label: 'Stone' },
    { value: 'bg-amber-700', label: 'Dark amber' },
];
